import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30, filter: 'blur(10px)' },
  visible: { opacity: 1, y: 0, filter: 'blur(0px)' }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

export default function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message envoyé avec succès');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-wrapper" data-testid="contact-page">
      {/* Hero Section */}
      <section className="hero-section relative min-h-[50vh] flex items-center overflow-hidden" data-testid="contact-hero">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_cf0aab81-969a-4e9c-9793-f40790513670/artifacts/ozk0drws_IMAGE_PAGE_GARDE_CONSOLIDATION.png')`,
            opacity: 0.7
          }} />

        <div className="hero-overlay" />
        <div className="hero-content w-full pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="max-w-3xl">

              <motion.p
                variants={fadeUp}
                transition={{ duration: 0.8 }}
                className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-4">

                Contact
              </motion.p>
              <motion.h1
                variants={fadeUp}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#FDFBF7] font-medium tracking-tight leading-tight mb-6">

                Prenons<br />
                <span className="text-gold-gradient">contact</span>
              </motion.h1>
              <motion.div
                variants={fadeUp}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="gold-line mb-8" />

              <motion.p
                variants={fadeUp}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-[#FDFBF7]/70 text-lg md:text-xl leading-relaxed">

                Nous serions ravis de prendre connaissance de vos projets 
                et de vous présenter notre approche.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 md:py-32 bg-[#FDFBF7]" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}>

              <p className="text-[#C5A059] text-xs tracking-[0.3em] uppercase mb-4">
                Notre bureau
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#0B1221] font-medium tracking-tight mb-6">
                Rendez-nous visite
              </h2>
              <div className="gold-line mb-10" />

              {/* Address Card */}
              <div className="bg-[#0B1221] p-8 md:p-10 relative mb-8" data-testid="address-card">
                <div className="flex items-start gap-4">
                  <MapPin size={24} className="text-[#C5A059] flex-shrink-0 mt-1" strokeWidth={1.5} />
                  <div>
                    <p className="text-[#C5A059] text-xs tracking-[0.2em] uppercase mb-4">
                      Siège social
                    </p>
                    <address className="font-serif text-lg text-[#FDFBF7] not-italic leading-relaxed">
                      12, Avenue de Maire<br />
                      7500 Tournai<br />
                      Belgique
                    </address>
                  </div>
                </div>
                {/* Corner brackets */}
                <div className="corner-bracket corner-bracket-tl absolute top-4 left-4" />
                <div className="corner-bracket corner-bracket-tr absolute top-4 right-4" />
                <div className="corner-bracket corner-bracket-bl absolute bottom-4 left-4" />
                <div className="corner-bracket corner-bracket-br absolute bottom-4 right-4" />
              </div>

              <p className="!font-semibold !text-justify text-[#64748B]">Notre équipe vous accueille exclusivement sur rendez-vous.</p>

              <div className="mt-10 pt-10 border-t border-[#C5A059]/20">
                <p className="!text-base !text-justify text-[#64748B]">
                  Pour toute demande d'information ou de rendez-vous, 
                  veuillez utiliser le formulaire ci-contre. Nous nous 
                  engageons à vous répondre dans les plus brefs délais.
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}>

              {isSubmitted ?
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-[#F5F2EB] p-10 md:p-12 text-center h-full flex flex-col items-center justify-center"
                data-testid="success-message">

                  <CheckCircle size={48} className="text-[#C5A059] mb-6" strokeWidth={1.5} />
                  <h3 className="font-serif text-2xl text-[#0B1221] mb-4">
                    Message Envoyé
                  </h3>
                  <p className="text-[#64748B] leading-relaxed mb-8">
                    Merci pour votre message. Notre équipe vous contactera 
                    dans les plus brefs délais.
                  </p>
                  <button
                  onClick={() => setIsSubmitted(false)}
                  className="text-[#0B1221] text-sm tracking-wider uppercase font-medium hover:text-[#C5A059] transition-colors">

                    Envoyer un autre message
                  </button>
                </motion.div> :

              <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-[#0B1221] text-xs tracking-wider uppercase mb-3">
                        Prénom *
                      </label>
                      <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-[#0B1221]/20 py-3 text-[#0B1221] placeholder:text-[#64748B]/50 focus:border-[#C5A059] focus:outline-none transition-colors"
                      placeholder="Votre prénom"
                      data-testid="input-firstName" />

                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-[#0B1221] text-xs tracking-wider uppercase mb-3">
                        Nom *
                      </label>
                      <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-[#0B1221]/20 py-3 text-[#0B1221] placeholder:text-[#64748B]/50 focus:border-[#C5A059] focus:outline-none transition-colors"
                      placeholder="Votre nom"
                      data-testid="input-lastName" />

                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-[#0B1221] text-xs tracking-wider uppercase mb-3">
                      Email *
                    </label>
                    <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b border-[#0B1221]/20 py-3 text-[#0B1221] placeholder:text-[#64748B]/50 focus:border-[#C5A059] focus:outline-none transition-colors"
                    placeholder="votre@email.com"
                    data-testid="input-email" />

                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-[#0B1221] text-xs tracking-wider uppercase mb-3">
                      Sujet
                    </label>
                    <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full bg-transparent border-b border-[#0B1221]/20 py-3 text-[#0B1221] placeholder:text-[#64748B]/50 focus:border-[#C5A059] focus:outline-none transition-colors"
                    placeholder="Objet de votre message"
                    data-testid="input-subject" />

                  </div>

                  <div>
                    <label htmlFor="message" className="block text-[#0B1221] text-xs tracking-wider uppercase mb-3">
                      Message *
                    </label>
                    <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full bg-transparent border-b border-[#0B1221]/20 py-3 text-[#0B1221] placeholder:text-[#64748B]/50 focus:border-[#C5A059] focus:outline-none transition-colors resize-none"
                    placeholder="Comment pouvons-nous vous aider ?"
                    data-testid="input-message" />

                  </div>

                  <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-gold inline-flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-button">

                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer le Message'}
                    <Send size={16} />
                  </button>
                </form>
              }
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section (Decorative) */}
      <section className="relative h-[400px] bg-[#0B1221]" data-testid="map-section">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1761437855598-011cf89b2ad4?crop=entropy&cs=srgb&fm=jpg&q=85')`
          }} />

        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center">

            <MapPin size={48} className="text-[#C5A059] mx-auto mb-4" strokeWidth={1} />
            <p className="font-serif text-2xl text-[#FDFBF7] mb-2">
              Tournai
            </p>
            <p className="text-[#FDFBF7]/50 text-sm tracking-wider uppercase">
              Belgique
            </p>
          </motion.div>
        </div>
      </section>
    </div>);

}